import React, { useMemo } from 'react';

import type { BoxProps } from '@withjoy/joykit';
import { Box, Flex, LinkV2, SpacingStack, TextV2 } from '@withjoy/joykit';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import type { EventTravelFragment } from '@graphql/fragments/event.travel.graphql.generated';
import { TravelType, EventPageVisibility } from '@graphql/generated-base';
import { useFetchTravelListData } from './useFetchTravelListData';
import { ContentColumns } from '../../primitives';
import type { ContentColumnProps } from '../../primitives/ContentColumn';
import { TravelBooking, travelTypeToComponent, travelTypeToIcon } from './TravelList.blocks';
import { TelemetryProvider } from './TravelList.telemetry';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { hed5ToSubHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useTranslation } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useLayout } from '@shared/context/LayoutProvider';
import { useWebsitePages } from '@shared/hooks/useWebsitePages';
import { useGuestPageContentRedesign } from '@apps/guest/hooks/useGuestPageContentRedesign';

export interface TravelListProps
  extends Readonly<{
      useMiniNav?: boolean;
      condensed?: boolean;
      eventId?: string;
      eventHandle: string;
      pages?: EventPageFragment[];
    }>,
    BoxProps {}

const _encodeNameToUrlHash = (name: string) => {
  return encodeURIComponent(name.trim().replace(/ /g, '-')).toLowerCase();
};

const TravelList = React.forwardRef<HTMLDivElement, TravelListProps>(({ eventId, useMiniNav = true, condensed, pages, ...restProps }, ref) => {
  const navItems: React.ReactElement[] = [];
  const containerColumnProps: ContentColumnProps = useMiniNav ? { span: [12, 'three-fifths'] } : { span: 12 };
  const { layout } = useLayout();
  const { eventHandle } = restProps;
  const { t } = useTranslation('guestSiteTravel');
  const { linkText } = t('accommodationLink');
  const travelAccommodationConnectionEnabled = useFeatureValue('travelAccommodationConnectionEnabled')?.value === 'on';
  const { data, loading } = useFetchTravelListData(eventHandle);

  const { enabled: isGuestPageContentRedesignEnabled } = useGuestPageContentRedesign();
  const { getPageByName } = useWebsitePages();
  const accommodationPage = getPageByName('accommodations');

  const itemComponentWrapperStyles = useMemo<ContentColumnProps>(() => {
    if (isGuestPageContentRedesignEnabled) {
      if (condensed) {
        return {
          span: [12],
          offset: 0,
          marginTop: 7,
        };
      }

      return {
        span: [12, null, 6],
        offset: 0,
        marginTop: { _: 7, md: 0 },
      };
    } else {
      if (condensed) {
        return {
          span: [12],
          offset: 0,
          marginTop: 6,
        };
      }
      return {
        span: [12, 10],
        offset: [0, 2],
        marginTop: 6,
      };
    }
  }, [condensed, isGuestPageContentRedesignEnabled]);

  if (loading) return <div style={{ height: '300px' }}></div>; // height set to prevent divs from jumping around while page loads

  const items = data?.eventByName?.info.travel || [];
  const travelItems =
    accommodationPage?.visibility === EventPageVisibility.public && travelAccommodationConnectionEnabled
      ? [
          ...items,
          {
            id: accommodationPage?.id,
            name: accommodationPage?.pageTitle as EventTravelFragment['name'],
            type: TravelType.hotel,
            url: `/${eventHandle}/${accommodationPage?.pageSlug}`,
            urlTitle: linkText(),
            isBrannan: layout === 'brannan',
          },
        ]
      : items;

  const listMarkup = travelItems.map((item: { type: EventTravelFragment['type']; id?: EventTravelFragment['id']; name?: EventTravelFragment['name'] }) => {
    const { id, name, type } = item;
    const ItemIcon = travelTypeToIcon[type];
    {
      /* TODO: figure out how to type travelTypeToComponent for booking */
    }
    const ItemComponent = item.type === 'booking' ? TravelBooking : travelTypeToComponent[type as keyof typeof travelTypeToComponent];

    if (!ItemIcon || !ItemComponent) {
      return null;
    }

    const anchorId = _encodeNameToUrlHash(name || '');

    // To be added in later
    if (useMiniNav) {
      navItems.push(
        <li>
          <LinkV2
            wordBreak="break-word"
            href={`#${anchorId}`}
            textDecoration={'none'}
            typographyVariant={'button3'}
          >
            {name}
          </LinkV2>
        </li>,
      );
    }

    return (
      <ContentColumns
        key={id}
        data-testid={id}
        id={anchorId}
      >
        <ContentColumns.Column span={!isGuestPageContentRedesignEnabled || condensed ? 12 : [12, null, 6]}>
          {condensed ? (
            // aloha
            <Flex
              flexDirection={['row', null, 'column']}
              marginTop={[5]}
              alignItems={'center'}
              justifyContent={['flex-start', null, 'center']}
            >
              <ItemIcon size={40} />
              <GuestSiteTypographyOverride override={hed5ToSubHeadingOverride}>
                <TextV2
                  typographyVariant="hed5"
                  fontWeight={700}
                  textAlign={['left', null, 'center']}
                  marginLeft={[3, null, 0]}
                  wordBreak="break-word"
                >
                  {name}
                </TextV2>
              </GuestSiteTypographyOverride>
            </Flex>
          ) : (
            // brannan
            <Flex
              flexDirection="row"
              marginTop={[5, null, 0]}
              alignItems={'center'}
              justifyContent={{ _: 'flex-start', md: 'initial' }}
            >
              <ItemIcon
                size={40}
                marginLeft={[0, null, 6]}
              />
              <GuestSiteTypographyOverride override={hed5ToSubHeadingOverride}>
                <TextV2
                  typographyVariant="hed5"
                  fontWeight={700}
                  marginLeft={[3, null, 5]}
                  wordBreak="break-word"
                >
                  {name}
                </TextV2>
              </GuestSiteTypographyOverride>
            </Flex>
          )}
        </ContentColumns.Column>

        <ContentColumns.Column {...itemComponentWrapperStyles}>
          {/*// @ts-expect-error - Props of each ItemComponent are auto-generated and a bit different. id and name are required in some components */}
          <ItemComponent travelItem={item} />
        </ContentColumns.Column>
      </ContentColumns>
    );
  });

  return (
    <TelemetryProvider context={{ eventId: eventId, layout: layout }}>
      <Box
        ref={ref}
        {...(!isGuestPageContentRedesignEnabled && {
          maxWidth: {
            _: 'auto',
            md: 504,
          },
          marginX: 'auto',
        })}
        {...restProps}
      >
        <ContentColumns>
          {useMiniNav && (
            <ContentColumns.Column span={'two-fifths'}>
              {/* <Box position="sticky" top={40} maxWidth={'75%'}>
              <SpacingStack as="ul" spacing={6}>
                {navItems}
              </SpacingStack>
            </Box> */}
            </ContentColumns.Column>
          )}
          <ContentColumns.Column {...containerColumnProps}>
            <SpacingStack spacing={10}>{listMarkup}</SpacingStack>
          </ContentColumns.Column>
        </ContentColumns>
      </Box>
    </TelemetryProvider>
  );
});

TravelList.displayName = 'TravelList';

export { TravelList };
